<template>
  <div class="app-evaluation-message">
    <div class="app-evaluation-message-content">
      <pre
        class="app-evaluation-message-text"
        :class="{ 'app-evaluation-message-text-folded': isFoldable && !expanded }"
        >{{ message }}</pre
      >
      <button v-if="isFoldable" type="button" class="app-evaluation-message-fold" @click="onToggleExpanded">
        <app-icon :icon="expanded ? 'chevron-up' : 'chevron-down'" />
        {{ $t(expanded ? "show-less" : "show-more") }}
      </button>
    </div>
    <button
      type="button"
      class="app-evaluation-message-copy"
      :title="$t('notification-copy-message').toString()"
      :aria-label="$t('notification-copy-message').toString()"
      @click="onCopyMessage"
    >
      <app-icon icon="clipboard" />
    </button>
  </div>
</template>

<script lang="ts">
import { Component, Prop } from "vue-property-decorator";
import AppIcon from "@/app/shared/components/app-icon/app-icon.vue";
import { BaseComponent } from "@/app/shared/components/base-component/base-component";
import { CatchError } from "@/app/shared/services/helper/catch-helper";

// Matches the folded preview height, so a message that fits is never folded.
const FOLDED_PREVIEW_LINES = 3;
const FOLDED_PREVIEW_CHARS = 300;

@Component({
  name: "app-evaluation-message",
  components: { AppIcon },
})
export default class AppEvaluationMessage extends BaseComponent {
  @Prop({ required: true }) message!: string;

  expanded = false;

  get isFoldable(): boolean {
    return this.message.split("\n").length > FOLDED_PREVIEW_LINES || this.message.length > FOLDED_PREVIEW_CHARS;
  }

  @CatchError()
  onToggleExpanded() {
    this.expanded = !this.expanded;
  }

  @CatchError()
  async onCopyMessage() {
    await navigator.clipboard.writeText(this.message);
    this.showSuccess(this.$t("notification-message-copied").toString());
  }
}
</script>

<style lang="scss">
.app-evaluation-message {
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  min-width: 0;

  &-content {
    flex: 1 1 auto;
    min-width: 0;
  }

  &-text {
    max-height: 24rem;
    margin: 0;
    padding: 0.4rem 0.5rem;
    overflow: auto;
    border: 1px solid var(--default-border-color);
    background-color: var(--default-bg-color);
    color: var(--default-text-color);
    font-size: 0.72rem;
    line-height: 1.35;
    white-space: pre-wrap;
    word-break: break-word;
    user-select: text;
    cursor: text;

    // Three lines of the preview: 3 × line-height + vertical padding.
    &-folded {
      max-height: calc(3 * 1.35em + 0.8rem);
      overflow: hidden;
    }
  }

  &-fold {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.15rem 0;
    border: 0;
    background: transparent;
    color: var(--action-text-color);
    font-size: 0.72rem;
    cursor: pointer;

    &:hover,
    &:focus {
      text-decoration: underline;
    }
  }

  &-copy {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: var(--muted-text-color);
    cursor: pointer;

    &:hover,
    &:focus {
      background-color: var(--secondary-bg-highlight-color);
      color: var(--action-text-color);
    }
  }
}
</style>
